import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AgentRouteService } from '../../../../services/forwarding/agent-route.service';
import { AgentRoutePricingService } from '../../../../services/forwarding/agent-route-pricing.service';
import { AgentService } from '../../../../services/forwarding/agent.service';
import { PortService } from '../../../../services/forwarding/port.service';
import { OperationalChargesService } from '../../../../services/forwarding/operational-charges.service';
import { AgentRoute } from '../../../../models/forwarding/agent-route.model';
import { Agent } from '../../../../models/forwarding/agent.model';
import { Port } from '../../../../models/forwarding/port.model';
import { AgentRoutePricing, CreateAgentRoutePricingDto } from '../../../../models/forwarding/agent-route-pricing.model';
import { OperationalCharge } from '../../../../models/forwarding/operational-charge.model';

@Component({
  selector: 'app-agent-pricing-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-pricing-modal.html',
  styleUrl: './agent-pricing-modal.css'
})
export class AgentPricingModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  showAddRouteModal = false;
  showManagePricingModal = false;

  // Data
  agentRoutes: AgentRoute[] = [];
  agents: Agent[] = [];
  ports: Port[] = [];
  filteredRoutes: AgentRoute[] = [];
  selectedRoute: AgentRoute | null = null;

  // Search
  searchTerm = '';

  // Add Route Form
  newRoute = {
    agentId: 0,
    originPortId: 0,
    destinationPortId: 0
  };

  // Statistics
  stats = {
    totalRoutes: 0,
    activeAgents: 0,
    uniquePorts: 0
  };

  // Pricing Management
  pricingData: AgentRoutePricing[] = [];
  operationalCharges: OperationalCharge[] = [];
  bulkValidFrom: string = '';
  bulkValidUntil: string = '';

  private subscriptions: Subscription[] = [];

  constructor(
    private agentRouteService: AgentRouteService,
    private agentRoutePricingService: AgentRoutePricingService,
    private agentService: AgentService,
    private portService: PortService,
    private operationalChargesService: OperationalChargesService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadData(): void {
    // Load agent routes
    this.subscriptions.push(
      this.agentRouteService.getAgentRoutes().subscribe({
        next: (routes) => {
          this.agentRoutes = routes;
          this.filteredRoutes = routes;
          this.calculateStats();
        },
        error: (error) => console.error('Error loading agent routes:', error)
      })
    );

    // Load agents
    this.subscriptions.push(
      this.agentService.getAgents().subscribe({
        next: (agents) => {
          this.agents = agents.filter(a => a.isActive);
        },
        error: (error) => console.error('Error loading agents:', error)
      })
    );

    // Load ports
    this.subscriptions.push(
      this.portService.getPorts().subscribe({
        next: (ports) => {
          this.ports = ports.filter(p => p.isActive);
        },
        error: (error) => console.error('Error loading ports:', error)
      })
    );
  }

  private calculateStats(): void {
    this.stats.totalRoutes = this.agentRoutes.length;
    this.stats.activeAgents = new Set(this.agentRoutes.map(r => r.agentId)).size;
    const allPorts = new Set<number>();
    this.agentRoutes.forEach(r => {
      allPorts.add(r.originPortId);
      allPorts.add(r.destinationPortId);
    });
    this.stats.uniquePorts = allPorts.size;
  }

  onSearch(): void {
    const term = this.searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredRoutes = this.agentRoutes;
      return;
    }

    this.filteredRoutes = this.agentRoutes.filter(route =>
      route.agentName?.toLowerCase().includes(term) ||
      route.originPortCode?.toLowerCase().includes(term) ||
      route.originPortName?.toLowerCase().includes(term) ||
      route.destinationPortCode?.toLowerCase().includes(term) ||
      route.destinationPortName?.toLowerCase().includes(term)
    );
  }

  open(): void {
    console.log('[AgentPricingModal] Opening modal');
    this.isOpen = true;
    this.agentRouteService.refresh();
  }

  close(): void {
    console.log('[AgentPricingModal] Closing modal');
    this.isOpen = false;
    this.showAddRouteModal = false;
    this.showManagePricingModal = false;
    this.selectedRoute = null;
  }

  openAddRouteModal(): void {
    this.newRoute = {
      agentId: 0,
      originPortId: 0,
      destinationPortId: 0
    };
    this.showAddRouteModal = true;
  }

  closeAddRouteModal(): void {
    this.showAddRouteModal = false;
  }

  createAgentRoute(): void {
    if (!this.newRoute.agentId || !this.newRoute.originPortId || !this.newRoute.destinationPortId) {
      alert('Please fill in all required fields');
      return;
    }

    this.agentRouteService.create({
      agentId: this.newRoute.agentId,
      originPortId: this.newRoute.originPortId,
      destinationPortId: this.newRoute.destinationPortId,
      isActive: true
    }).subscribe({
      next: () => {
        console.log('Agent route created successfully');
        this.closeAddRouteModal();
        this.agentRouteService.refresh();
      },
      error: (error) => {
        console.error('Error creating agent route:', error);
        alert('Error creating agent route: ' + (error.error?.message || error.message));
      }
    });
  }

  openManagePricing(route: AgentRoute): void {
    this.selectedRoute = route;
    this.showManagePricingModal = true;
    this.loadPricingData(route.id);
  }

  closeManagePricingModal(): void {
    this.showManagePricingModal = false;
    this.selectedRoute = null;
    this.pricingData = [];
    this.bulkValidFrom = '';
    this.bulkValidUntil = '';
  }

  private loadPricingData(agentRouteId: number): void {
    // Load operational charges (only OC and OF codes for agent pricing)
    this.subscriptions.push(
      this.operationalChargesService.getCharges().subscribe({
        next: (charges: OperationalCharge[]) => {
          this.operationalCharges = charges.filter((c: OperationalCharge) =>
            c.isActive && (c.chargeCode === 'OC' || c.chargeCode === 'OF')
          );
          this.initializePricingData(agentRouteId);
        },
        error: (error: any) => console.error('Error loading operational charges:', error)
      })
    );
  }

  private initializePricingData(agentRouteId: number): void {
    // Load existing pricing for this route
    this.subscriptions.push(
      this.agentRoutePricingService.getByAgentRouteId(agentRouteId).subscribe({
        next: (existingPricing: AgentRoutePricing[]) => {
          // Create pricing entries for all operational charges
          this.pricingData = this.operationalCharges.map((charge: OperationalCharge) => {
            const existing = existingPricing.find((p: AgentRoutePricing) => p.operationalChargeId === charge.id);
            if (existing) {
              return existing;
            } else {
              // Create new empty pricing entry
              return {
                id: 0,
                agentRouteId: agentRouteId,
                operationalChargeId: charge.id,
                operationalChargeName: charge.feeName,
                operationalChargeCode: charge.chargeCode,
                currency: charge.currency || 'PHP',
                chargeBasis: charge.chargeBasis || 'Fixed Amount',
                fcl20ft: 0,
                fcl40ft: 0,
                lcl: 0,
                validFrom: undefined,
                validUntil: undefined,
                isActive: true,
                createdAt: new Date(),
                updatedAt: undefined
              } as AgentRoutePricing;
            }
          });
        },
        error: (error: any) => console.error('Error loading pricing data:', error)
      })
    );
  }

  applyBulkValidity(): void {
    if (!this.bulkValidFrom || !this.bulkValidUntil) {
      alert('Please select both valid from and valid until dates');
      return;
    }

    // Apply dates to all pricing entries
    this.pricingData.forEach((pricing: AgentRoutePricing) => {
      pricing.validFrom = new Date(this.bulkValidFrom);
      pricing.validUntil = new Date(this.bulkValidUntil);
    });

    console.log('Bulk validity dates applied to all charges');
  }

  savePricing(): void {
    if (!this.selectedRoute) {
      alert('No route selected');
      return;
    }

    // Filter out pricing entries that have at least one price set
    const pricingToSave = this.pricingData.filter((p: AgentRoutePricing) =>
      (p.fcl20ft && p.fcl20ft > 0) ||
      (p.fcl40ft && p.fcl40ft > 0) ||
      (p.lcl && p.lcl > 0)
    );

    if (pricingToSave.length === 0) {
      alert('Please enter at least one price before saving');
      return;
    }

    // Save each pricing entry
    let savedCount = 0;
    let errorCount = 0;

    pricingToSave.forEach((pricing: AgentRoutePricing) => {
      if (pricing.id && pricing.id > 0) {
        // Update existing
        const updateDto = {
          operationalChargeId: pricing.operationalChargeId,
          currency: pricing.currency,
          chargeBasis: pricing.chargeBasis,
          fcl20ft: pricing.fcl20ft,
          fcl40ft: pricing.fcl40ft,
          lcl: pricing.lcl,
          validFrom: pricing.validFrom,
          validUntil: pricing.validUntil,
          isActive: pricing.isActive
        };

        this.agentRoutePricingService.update(pricing.id, updateDto).subscribe({
          next: () => {
            savedCount++;
            if (savedCount + errorCount === pricingToSave.length) {
              this.showSaveResult(savedCount, errorCount);
            }
          },
          error: (error: any) => {
            console.error('Error updating pricing:', error);
            errorCount++;
            if (savedCount + errorCount === pricingToSave.length) {
              this.showSaveResult(savedCount, errorCount);
            }
          }
        });
      } else {
        // Create new
        if (!this.selectedRoute) return; // Safety check

        const createDto: CreateAgentRoutePricingDto = {
          agentRouteId: this.selectedRoute.id,
          operationalChargeId: pricing.operationalChargeId,
          currency: pricing.currency,
          chargeBasis: pricing.chargeBasis,
          fcl20ft: pricing.fcl20ft,
          fcl40ft: pricing.fcl40ft,
          lcl: pricing.lcl,
          validFrom: pricing.validFrom,
          validUntil: pricing.validUntil,
          isActive: true
        };

        this.agentRoutePricingService.create(createDto).subscribe({
          next: () => {
            savedCount++;
            if (savedCount + errorCount === pricingToSave.length) {
              this.showSaveResult(savedCount, errorCount);
            }
          },
          error: (error: any) => {
            console.error('Error creating pricing:', error);
            errorCount++;
            if (savedCount + errorCount === pricingToSave.length) {
              this.showSaveResult(savedCount, errorCount);
            }
          }
        });
      }
    });
  }

  private showSaveResult(savedCount: number, errorCount: number): void {
    if (errorCount === 0) {
      alert(`Successfully saved ${savedCount} pricing entries`);
      this.closeManagePricingModal();
    } else {
      alert(`Saved ${savedCount} entries, but ${errorCount} failed. Please check console for details.`);
    }
  }

  deleteRoute(route: AgentRoute): void {
    if (!confirm(`Are you sure you want to delete the route for ${route.agentName} from ${route.originPortCode} to ${route.destinationPortCode}?`)) {
      return;
    }

    this.agentRouteService.delete(route.id).subscribe({
      next: () => {
        console.log('Agent route deleted successfully');
        this.agentRouteService.refresh();
      },
      error: (error) => {
        console.error('Error deleting agent route:', error);
        alert('Error deleting agent route: ' + (error.error?.message || error.message));
      }
    });
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  }
}
